import React from 'react';
import { useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { joiResolver } from '@hookform/resolvers/joi';
import Joi from 'joi';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import InputAdornment from '@mui/material/InputAdornment';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import { SFPalette } from '@/lib/color';
import SFVisibilityIcon from '@/component/icons/sf-visiblity-icon';
import SFVisibilityOffIcon from '@/component/icons/sf-visiblity-off-icon';
import Container from '@mui/material/Container';
import TopBackButton from '@/component/common/top-back-button';
import Box from '@mui/material/Box';
import { fontWeight } from '@/theme';
import SFAlertDialog from '@/component/common/sf-alert-dialog';
import { loginLocalStorage } from '@/lib/storage';
import { changePw } from '@/api/server/user/update';
import ErrorRes from '@/api/server/ErrorRes';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import BottomFix from '@/component/common/bottom-fix';
import { useTranslation } from 'react-i18next';

type FormDataType = {
  currentPassword: string;
  newPassword: string;
  confirmNewPassword: string;
};

const schema = Joi.object({
  currentPassword: Joi.string()
    .pattern(new RegExp('^(?=.*[a-z])(?=.*\\d)(?=.*[@$!%*?&])[A-Za-z\\d@$!%*?&]{8,16}$'))
    .required(),
  newPassword: Joi.string()
    .pattern(new RegExp('^(?=.*[a-z])(?=.*\\d)(?=.*[@$!%*?&])[A-Za-z\\d@$!%*?&]{8,16}$'))
    .required(),
  confirmNewPassword: Joi.string()
    .valid(Joi.ref('newPassword'))
    .required()
    .messages({ 'any.only': '비밀번호가 일치하지 않습니다.' }),
});

export default function ChangePw() {
  const { t } = useTranslation();
  const {
    handleSubmit,
    control,
    watch,
    formState: { errors },
  } = useForm<FormDataType>({
    resolver: joiResolver(schema),
    mode: 'onChange',
  });
  const navigate = useNavigate();
  const [openDialog, setOpenDialog] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmNewPassword, setShowConfirmNewPassword] = useState(false);

  const handleClickShowCurrentPassword = () => setShowCurrentPassword(!showCurrentPassword);
  const handleClickShowNewPassword = () => setShowNewPassword(!showNewPassword);
  const handleClickShowConfirmNewPassword = () => setShowConfirmNewPassword(!showConfirmNewPassword);

  const handleMouseDownPassword = (event: React.MouseEvent<HTMLButtonElement>) => event.preventDefault();

  /**
   * @todo 비밀번호 변경 서버 통신 로직
   */
  const onSubmit = async (data: FormDataType) => {
    const { confirmNewPassword, currentPassword, newPassword } = data;
    if (confirmNewPassword !== newPassword) {
      toast.error(`${t('toast.pwNotMatch')}`);
      return;
    }
    if (currentPassword === newPassword) {
      toast.error(`${t('toast.samePw')}`);
      return;
    }
    const email = loginLocalStorage().email;
    const payload = {
      email,
      current_pw: currentPassword,
      now_pw: newPassword,
    };
    const re = await changePw(payload);
    if (re.ret_code !== 200) {
      ErrorRes({ code: re.ret_code, msg: re.msg, isBack: false });
      return;
    }
    setOpenDialog(true);
  };

  const currentPassword = watch('currentPassword');
  const newPassword = watch('newPassword');
  const confirmNewPassword = watch('confirmNewPassword');
  return (
    <Container maxWidth="sm">
      <TopBackButton
        title={t('title.changePw')}
        boxProps={{
          mb: 4,
        }}
      />
      <Typography
        variant="body1"
        mb={3}
        sx={{
          wordBreak: 'keep-all',
          color: SFPalette.grey2,
          fontWeight: fontWeight.Medium,
        }}
      >
        {t('message.requireExistingPw')}
      </Typography>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Stack rowGap={3.5} mb={5}>
          <Controller
            name="currentPassword"
            control={control}
            defaultValue=""
            render={({ field }) => (
              <div>
                <Typography variant="body1" mb={1}>
                  <strong>{t('validation.CurrentPw')}</strong>
                </Typography>
                <TextField
                  {...field}
                  fullWidth
                  type={showCurrentPassword ? 'text' : 'password'}
                  placeholder={t('placeholder.currentPw')}
                  error={!!errors.currentPassword}
                  helperText={t('message.requirePw')}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={handleClickShowCurrentPassword} onMouseDown={handleMouseDownPassword}>
                          {showCurrentPassword ? <SFVisibilityIcon /> : <SFVisibilityOffIcon />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  FormHelperTextProps={{
                    style: {
                      color: errors.currentPassword
                        ? 'red'
                        : currentPassword
                        ? SFPalette.shuffleColor2
                        : SFPalette.lightGrey2,
                    },
                  }}
                />
              </div>
            )}
          />

          <Box>
            <Typography
              variant="body1"
              mb={2.5}
              sx={{
                wordBreak: 'keep-all',
                color: SFPalette.grey2,
                fontWeight: fontWeight.Medium,
              }}
            >
              {t('message.wantchangePw')}
            </Typography>
            <Controller
              name="newPassword"
              control={control}
              defaultValue=""
              render={({ field }) => (
                <div>
                  <Typography variant="body1" mb={1}>
                    <strong>{t('validation.newPw')}</strong>
                  </Typography>
                  <TextField
                    {...field}
                    fullWidth
                    type={showNewPassword ? 'text' : 'password'}
                    placeholder={t('placeholder.newPw')}
                    error={!!errors.newPassword}
                    helperText={t('message.requirePw')}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton onClick={handleClickShowNewPassword} onMouseDown={handleMouseDownPassword}>
                            {showNewPassword ? <SFVisibilityIcon /> : <SFVisibilityOffIcon />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                    FormHelperTextProps={{
                      style: {
                        color: errors.newPassword
                          ? 'red'
                          : newPassword
                          ? SFPalette.shuffleColor2
                          : SFPalette.lightGrey2,
                      },
                    }}
                  />
                </div>
              )}
            />
          </Box>

          <Controller
            name="confirmNewPassword"
            control={control}
            defaultValue=""
            render={({ field }) => (
              <div>
                <Typography variant="body1" mb={1}>
                  <strong>{t('validation.checkNewPw')}</strong>
                </Typography>
                <TextField
                  {...field}
                  fullWidth
                  placeholder={t('placeholder.reNewPw')}
                  type={showConfirmNewPassword ? 'text' : 'password'}
                  error={!!errors.confirmNewPassword}
                  helperText={
                    !confirmNewPassword
                      ? ''
                      : errors.confirmNewPassword
                      ? `${t('message.pwNotMatch')}`
                      : `${t('message.pwMatch')}`
                  }
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={handleClickShowConfirmNewPassword} onMouseDown={handleMouseDownPassword}>
                          {showConfirmNewPassword ? <SFVisibilityIcon /> : <SFVisibilityOffIcon />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  FormHelperTextProps={{
                    style: {
                      color: errors.confirmNewPassword
                        ? 'red'
                        : confirmNewPassword
                        ? SFPalette.shuffleColor2
                        : SFPalette.lightGrey2,
                    },
                  }}
                />
              </div>
            )}
          />
        </Stack>

        <BottomFix>
          <Button
            variant="contained"
            type="submit"
            disabled={Object.keys(errors).length !== 0}
            size="large"
            fullWidth
            sx={{
              my: 2,
            }}
          >
            {t('button.changePw')}
          </Button>
        </BottomFix>
      </form>
      <SFAlertDialog
        open={openDialog}
        onClose={() => {
          setOpenDialog(false);
          navigate('/securityCenter');
        }}
        fullWidth
      >
        <Typography variant="h6" textAlign="center" fontWeight={fontWeight.Medium}>
          {t('message.completedChangePw')}
        </Typography>
      </SFAlertDialog>
    </Container>
  );
}
